import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import { logAuditAction } from '../../utils/auditLogger';

const CATEGORIES = [
  { value: 'all', label: 'All Topics', icon: 'LayoutGrid' },
  { value: 'reservations', label: 'Reservations', icon: 'ClipboardList' },
  { value: 'check_in', label: 'Check-In', icon: 'LogIn' },
  { value: 'check_out', label: 'Check-Out', icon: 'LogOut' },
  { value: 'restaurant', label: 'Restaurant', icon: 'UtensilsCrossed' },
  { value: 'internet', label: 'Internet', icon: 'Wifi' },
  { value: 'residence_rules', label: 'Residence Rules', icon: 'BookOpen' },
  { value: 'visitor_policies', label: 'Visitor Policies', icon: 'UserCheck' },
  { value: 'payments', label: 'Payments', icon: 'CreditCard' },
  { value: 'services', label: 'Services', icon: 'Wrench' },
];

const EMPTY_FAQ = { question: '', answer: '', category: 'reservations', is_active: true, sort_order: 0 };

export default function HelpCenter() {
  const { user } = useAuth();
  const { isAdmin } = useRBAC();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  // Admin state
  const [editingFaq, setEditingFaq] = useState(null); // null = not editing, {} = new, faq = editing existing
  const [editForm, setEditForm] = useState(EMPTY_FAQ);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [saveError, setSaveError] = useState('');

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase?.from('faqs')?.select('*')?.order('category')?.order('sort_order');
      if (!isAdmin) query = query?.eq('is_active', true);
      const { data, error } = await query;
      if (!error) setFaqs(data || []);
    } catch (err) {
      console.error('fetchFaqs error:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  const filtered = faqs?.filter(faq => {
    const matchCat = activeCategory === 'all' || faq?.category === activeCategory;
    const matchSearch = !searchQuery || faq?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) || faq?.answer?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchCat && matchSearch;
  });

  const grouped = filtered?.reduce((acc, faq) => {
    const cat = faq?.category;
    if (!acc?.[cat]) acc[cat] = [];
    acc?.[cat]?.push(faq);
    return acc;
  }, {});

  const getCategoryLabel = (cat) => CATEGORIES?.find(c => c?.value === cat)?.label || cat;
  const getCategoryIcon = (cat) => CATEGORIES?.find(c => c?.value === cat)?.icon || 'HelpCircle';

  const handleAddNew = () => {
    setEditForm({ ...EMPTY_FAQ });
    setEditingFaq('new');
    setSaveError('');
  };

  const handleEdit = (faq) => {
    setEditForm({ question: faq?.question, answer: faq?.answer, category: faq?.category, is_active: faq?.is_active, sort_order: faq?.sort_order || 0 });
    setEditingFaq(faq);
    setSaveError('');
  };

  const handleSave = async () => {
    if (!editForm?.question?.trim() || !editForm?.answer?.trim()) {
      setSaveError('Question and answer are required.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      if (editingFaq === 'new') {
        const { data, error } = await supabase?.from('faqs')?.insert(editForm)?.select()?.single();
        if (error) throw error;
        await logAuditAction({ action: 'CREATE', module: 'help_center', recordId: data?.id, recordSummary: `Added FAQ: "${editForm?.question?.substring(0, 60)}"`, user });
      } else {
        const { error } = await supabase?.from('faqs')?.update(editForm)?.eq('id', editingFaq?.id);
        if (error) throw error;
        await logAuditAction({ action: 'UPDATE', module: 'help_center', recordId: editingFaq?.id, recordSummary: `Updated FAQ: "${editForm?.question?.substring(0, 60)}"`, user });
      }
      setEditingFaq(null);
      fetchFaqs();
    } catch (err) {
      setSaveError(err?.message || 'Failed to save FAQ.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (faqId, question) => {
    try {
      const { error } = await supabase?.from('faqs')?.delete()?.eq('id', faqId);
      if (error) throw error;
      await logAuditAction({ action: 'DELETE', module: 'help_center', recordId: faqId, recordSummary: `Deleted FAQ: "${question?.substring(0, 60)}"`, user });
      setDeleteConfirmId(null);
      fetchFaqs();
    } catch (err) {
      console.error('Delete FAQ error:', err);
    }
  };

  const handleToggleActive = async (faq) => {
    try {
      const { error } = await supabase?.from('faqs')?.update({ is_active: !faq?.is_active })?.eq('id', faq?.id);
      if (error) throw error;
      await logAuditAction({ action: 'UPDATE', module: 'help_center', recordId: faq?.id, recordSummary: `${faq?.is_active ? 'Deactivated' : 'Activated'} FAQ: "${faq?.question?.substring(0, 60)}"`, user });
      fetchFaqs();
    } catch (err) {
      console.error('Toggle FAQ error:', err);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="text-center py-8 px-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Icon name="HelpCircle" size={28} className="text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Help Center</h1>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">Find answers to common questions about residence life, services, and policies.</p>
          <div className="relative max-w-md mx-auto mt-5">
            <Icon name="Search" size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e?.target?.value)}
              placeholder="Search questions..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 shadow-sm"
            />
          </div>
        </div>

        {/* Admin Add Button */}
        {isAdmin && (
          <div className="flex justify-end">
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Icon name="Plus" size={16} />
              Add FAQ
            </button>
          </div>
        )}

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 justify-center">
          {CATEGORIES?.map(cat => (
            <button
              key={cat?.value}
              onClick={() => setActiveCategory(cat?.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                activeCategory === cat?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon name={cat?.icon} size={12} />
              {cat?.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
          </div>
        ) : filtered?.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Icon name="SearchX" size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">No results found</p>
            <p className="text-sm mt-1">Try a different search term or category.</p>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-8">
            {Object.entries(grouped)?.map(([category, items]) => (
              <div key={category}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={getCategoryIcon(category)} size={14} className="text-primary" />
                  </div>
                  <h2 className="text-sm font-semibold text-foreground">{getCategoryLabel(category)}</h2>
                  <span className="text-xs text-muted-foreground">({items?.length})</span>
                </div>
                <div className="space-y-2">
                  {items?.map(faq => (
                    <div key={faq?.id} className={`bg-card border rounded-xl overflow-hidden ${!faq?.is_active ? 'opacity-60 border-dashed' : 'border-border'}`}>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setExpandedId(expandedId === faq?.id ? null : faq?.id)}
                          className="flex-1 flex items-center justify-between gap-4 p-4 text-left hover:bg-muted/30 transition-colors"
                        >
                          <span className="text-sm font-medium text-foreground">{faq?.question}</span>
                          <Icon
                            name={expandedId === faq?.id ? 'ChevronUp' : 'ChevronDown'}
                            size={16}
                            className="text-muted-foreground flex-shrink-0 transition-transform"
                          />
                        </button>
                        {/* Admin inline actions */}
                        {isAdmin && (
                          <div className="flex items-center gap-1 pr-3 flex-shrink-0">
                            <button
                              onClick={() => handleToggleActive(faq)}
                              title={faq?.is_active ? 'Deactivate' : 'Activate'}
                              className={`p-1.5 rounded-lg transition-colors ${faq?.is_active ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20' : 'text-muted-foreground hover:bg-muted'}`}
                            >
                              <Icon name={faq?.is_active ? 'Eye' : 'EyeOff'} size={14} />
                            </button>
                            <button
                              onClick={() => handleEdit(faq)}
                              title="Edit"
                              className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            >
                              <Icon name="Pencil" size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(faq?.id)}
                              title="Delete"
                              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                              <Icon name="Trash2" size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      {expandedId === faq?.id && (
                        <div className="px-4 pb-4 border-t border-border">
                          <p className="text-sm text-muted-foreground leading-relaxed pt-3">{faq?.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contact Support */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-6 text-center">
            <Icon name="MessageCircle" size={24} className="text-primary mx-auto mb-3" />
            <h3 className="font-semibold text-foreground mb-1">Still need help?</h3>
            <p className="text-sm text-muted-foreground mb-4">Contact the residence administration directly.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href="mailto:residence@usenghor.org" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
                <Icon name="Mail" size={14} />
                Email Support
              </a>
              <a href="tel:+221338490020" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm text-foreground hover:bg-muted transition-colors">
                <Icon name="Phone" size={14} />
                Call Administration
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Edit/Add Modal */}
      {editingFaq !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-foreground">{editingFaq === 'new' ? 'Add New FAQ' : 'Edit FAQ'}</h2>
                <button onClick={() => setEditingFaq(null)} className="p-1 rounded-lg hover:bg-muted transition-colors">
                  <Icon name="X" size={18} className="text-muted-foreground" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Category</label>
                  <select value={editForm?.category} onChange={e => setEditForm(f => ({ ...f, category: e?.target?.value }))} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none">
                    {CATEGORIES?.filter(c => c?.value !== 'all')?.map(c => <option key={c?.value} value={c?.value}>{c?.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Question *</label>
                  <input type="text" value={editForm?.question} onChange={e => setEditForm(f => ({ ...f, question: e?.target?.value }))} placeholder="Enter the question..." className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Answer *</label>
                  <textarea value={editForm?.answer} onChange={e => setEditForm(f => ({ ...f, answer: e?.target?.value }))} rows={4} placeholder="Enter the answer..." className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="faq-active" checked={editForm?.is_active} onChange={e => setEditForm(f => ({ ...f, is_active: e?.target?.checked }))} className="rounded" />
                  <label htmlFor="faq-active" className="text-sm text-foreground">Active (visible to users)</label>
                </div>
                {saveError && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 text-sm">
                    <Icon name="AlertCircle" size={14} />
                    {saveError}
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={handleSave} disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
                  {saving ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Save" size={14} />}
                  {saving ? 'Saving...' : 'Save FAQ'}
                </button>
                <button onClick={() => setEditingFaq(null)} className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Icon name="Trash2" size={18} className="text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Delete FAQ</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { const faq = faqs?.find(f => f?.id === deleteConfirmId); handleDelete(deleteConfirmId, faq?.question || ''); }} className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors">Delete</button>
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
